// ============================================================
// Portal sign-in, invite acceptance, password reset — branded, no YOM chrome.
// ============================================================

import { useState, type FormEvent, type ReactNode } from 'react'
import { Link as RouterLink, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { Alert, Box, Button, Card, CardContent, Link, TextField, Typography } from '@mui/material'

import {
    usePortalAcceptMutation,
    usePortalForgotMutation,
    usePortalLoginMutation,
    usePortalResetMutation,
} from '@/apis/portalApi'
import { PASSWORD_MAX, passwordProblem, type PortalSessionResponse } from '@/apis/portalTypes'
import { v2ErrorMessage } from '@/apis/v2Mappers'
import { useAppDispatch, useAppSelector } from '@/app/hooks'

import { PortalBrand, PoweredBy } from './PortalChrome'
import { portalPath, usePortal } from './portalContext'
import { portalSignedIn, sessionForPortal } from './portalSessionSlice'

function AuthShell({
    title,
    subtitle,
    children,
}: {
    title: string
    subtitle?: string
    children: ReactNode
}) {
    const { portal } = usePortal()
    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'background.default',
                p: 2,
            }}
        >
            <Card
                sx={{ width: '100%', maxWidth: 420, borderTop: 4, borderTopColor: 'primary.main' }}
            >
                <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                        <PortalBrand />
                    </Box>
                    <Typography variant="h5" sx={{ mb: 0.5, textAlign: 'center' }}>
                        {title}
                    </Typography>
                    {subtitle && (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 3, textAlign: 'center' }}
                        >
                            {subtitle}
                        </Typography>
                    )}
                    {children}
                </CardContent>
            </Card>
            {portal.branding.supportEmail && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
                    Need help?{' '}
                    <Link href={`mailto:${portal.branding.supportEmail}`}>
                        {portal.branding.supportEmail}
                    </Link>
                </Typography>
            )}
            <PoweredBy show={portal.powered_by} />
        </Box>
    )
}

/** Sign the session in and go to the reports. */
function useFinishSignIn() {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const { basePath } = usePortal()
    return (session: PortalSessionResponse) => {
        dispatch(portalSignedIn(session))
        navigate(portalPath(basePath), { replace: true })
    }
}

export function PortalLoginPage() {
    const { portal, basePath } = usePortal()
    const session = sessionForPortal(
        useAppSelector(s => s.portalSession),
        portal.id,
    )
    const [login, state] = usePortalLoginMutation()
    const finish = useFinishSignIn()
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')

    if (session) {
        return <Navigate to={portalPath(basePath)} replace />
    }

    const submit = async (e: FormEvent) => {
        e.preventDefault()
        try {
            finish(await login({ portal_id: portal.id, email: email.trim(), password }).unwrap())
        } catch {
            // shown from state.error
        }
    }

    return (
        <AuthShell title="Sign in" subtitle={`Sign in to ${portal.branding.productName}`}>
            <Box component="form" onSubmit={submit} sx={{ display: 'grid', gap: 2 }}>
                {state.error && <Alert severity="error">{v2ErrorMessage(state.error)}</Alert>}
                <TextField
                    label="Email"
                    type="email"
                    autoComplete="email"
                    required
                    autoFocus
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                />
                <TextField
                    label="Password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                />
                <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={state.isLoading || !email.trim() || !password}
                >
                    Sign in
                </Button>
                <Link
                    component={RouterLink}
                    to={portalPath(basePath, 'forgot')}
                    variant="body2"
                    sx={{ textAlign: 'center' }}
                >
                    Forgot your password?
                </Link>
            </Box>
        </AuthShell>
    )
}

export function PortalForgotPage() {
    const { portal, basePath } = usePortal()
    const [forgot, state] = usePortalForgotMutation()
    const [email, setEmail] = useState('')

    const submit = async (e: FormEvent) => {
        e.preventDefault()
        try {
            await forgot({ portal_id: portal.id, email: email.trim() }).unwrap()
        } catch {
            // shown from state.error
        }
    }

    return (
        <AuthShell
            title="Reset your password"
            subtitle="We'll email you a link to choose a new password."
        >
            {state.isSuccess ? (
                <Alert severity="success" sx={{ mb: 2 }}>
                    If an account exists for {email.trim()}, a reset link is on its way. It expires
                    in one hour.
                </Alert>
            ) : (
                <Box component="form" onSubmit={submit} sx={{ display: 'grid', gap: 2, mb: 2 }}>
                    {state.error && <Alert severity="error">{v2ErrorMessage(state.error)}</Alert>}
                    <TextField
                        label="Email"
                        type="email"
                        autoComplete="email"
                        required
                        autoFocus
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                    />
                    <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        disabled={state.isLoading || !email.trim()}
                    >
                        Send reset link
                    </Button>
                </Box>
            )}
            <Box sx={{ textAlign: 'center' }}>
                <Link component={RouterLink} to={portalPath(basePath, 'login')} variant="body2">
                    Back to sign in
                </Link>
            </Box>
        </AuthShell>
    )
}

function NewPasswordForm({
    withName,
    submitLabel,
    busy,
    error,
    onSubmit,
}: {
    withName?: boolean
    submitLabel: string
    busy: boolean
    error: unknown
    onSubmit: (v: { password: string; name: string }) => void
}) {
    const [name, setName] = useState('')
    const [password, setPassword] = useState('')
    const [confirm, setConfirm] = useState('')
    const [touched, setTouched] = useState(false)
    const problem = passwordProblem(password, confirm)

    return (
        <Box
            component="form"
            onSubmit={(e: FormEvent) => {
                e.preventDefault()
                setTouched(true)
                if (!problem) {
                    onSubmit({ password, name: name.trim() })
                }
            }}
            sx={{ display: 'grid', gap: 2 }}
        >
            {!!error && <Alert severity="error">{v2ErrorMessage(error)}</Alert>}
            {withName && (
                <TextField
                    label="Your name (optional)"
                    autoComplete="name"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    slotProps={{ htmlInput: { maxLength: 100 } }}
                />
            )}
            <TextField
                label="New password"
                type="password"
                autoComplete="new-password"
                required
                value={password}
                onChange={e => setPassword(e.target.value)}
                helperText="At least 8 characters"
                slotProps={{ htmlInput: { maxLength: PASSWORD_MAX } }}
            />
            <TextField
                label="Confirm password"
                type="password"
                autoComplete="new-password"
                required
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                onBlur={() => setTouched(true)}
                error={touched && !!problem}
                helperText={touched && problem ? problem : ' '}
                slotProps={{ htmlInput: { maxLength: PASSWORD_MAX } }}
            />
            <Button type="submit" variant="contained" size="large" disabled={busy}>
                {submitLabel}
            </Button>
        </Box>
    )
}

function MissingToken({ what }: { what: string }) {
    const { basePath } = usePortal()
    return (
        <>
            <Alert severity="warning" sx={{ mb: 2 }}>
                This {what} link is incomplete. Open the link from your email again.
            </Alert>
            <Box sx={{ textAlign: 'center' }}>
                <Link component={RouterLink} to={portalPath(basePath, 'login')} variant="body2">
                    Go to sign in
                </Link>
            </Box>
        </>
    )
}

export function PortalAcceptPage() {
    const { portal } = usePortal()
    const [params] = useSearchParams()
    const token = params.get('token') ?? ''
    const [accept, state] = usePortalAcceptMutation()
    const finish = useFinishSignIn()

    return (
        <AuthShell
            title="Welcome"
            subtitle={`Choose a password to open your ${portal.branding.productName} reports.`}
        >
            {token ? (
                <NewPasswordForm
                    withName
                    submitLabel="Set password & continue"
                    busy={state.isLoading}
                    error={state.error}
                    onSubmit={async ({ password, name }) => {
                        try {
                            finish(
                                await accept({ token, password, name: name || undefined }).unwrap(),
                            )
                        } catch {
                            // shown from state.error
                        }
                    }}
                />
            ) : (
                <MissingToken what="invitation" />
            )}
        </AuthShell>
    )
}

export function PortalResetPage() {
    const [params] = useSearchParams()
    const token = params.get('token') ?? ''
    const [reset, state] = usePortalResetMutation()
    const finish = useFinishSignIn()

    return (
        <AuthShell title="Choose a new password">
            {token ? (
                <NewPasswordForm
                    submitLabel="Save password & sign in"
                    busy={state.isLoading}
                    error={state.error}
                    onSubmit={async ({ password }) => {
                        try {
                            finish(await reset({ token, password }).unwrap())
                        } catch {
                            // shown from state.error
                        }
                    }}
                />
            ) : (
                <MissingToken what="reset" />
            )}
        </AuthShell>
    )
}
